import { diagnosticLog } from './diagnosticLogger';

export type LocalLibraryHealthClassification = 'benign_empty' | 'genuine';

export type LocalLibraryHealthNoticeContext = {
  scanActive: boolean;
  albumCount: number | null;
};

export type LocalLibraryHealthNotice = {
  classification: LocalLibraryHealthClassification;
  detailSignature: string;
  messageKey: 'library_health.benign_empty' | 'library_health.genuine_warning';
  severity: 'info' | 'warning';
};

const BOB_FIELD_CASE =
  'Local library health check failed: stable id roundtrip error: album query failed: Query returned no rows';

/** Matches stable-id roundtrip failures caused by an empty catalog during first scan. */
export function matchesBenignEmptyHealthSignature(detail: string): boolean {
  const normalized = String(detail || '').trim().toLowerCase();
  if (!normalized) return false;
  const hasRoundtrip = normalized.includes('stable id roundtrip');
  const hasNoRows =
    normalized.includes('query returned no rows')
    || normalized.includes('returned no rows');
  return hasRoundtrip && hasNoRows;
}

/** Fleet-countable signature bucket for structured logs. */
export function extractHealthDetailSignature(detail: string): string {
  if (matchesBenignEmptyHealthSignature(detail)) return 'stable_id_roundtrip_no_rows';
  const normalized = String(detail || '').trim().toLowerCase();
  if (normalized.includes('stable id roundtrip')) return 'stable_id_roundtrip_other';
  if (normalized.includes('query returned no rows') || normalized.includes('returned no rows')) {
    return 'query_no_rows_other';
  }
  return 'other';
}

export function classifyLocalLibraryHealthFailure(
  detail: string,
  context: LocalLibraryHealthNoticeContext,
): LocalLibraryHealthClassification {
  if (!matchesBenignEmptyHealthSignature(detail)) return 'genuine';

  const albums = context.albumCount;
  const zeroAlbumLibrary = albums != null && Number.isFinite(albums) && albums <= 0;
  if (context.scanActive || zeroAlbumLibrary) return 'benign_empty';
  return 'genuine';
}

export function resolveLocalLibraryHealthNotice(
  detail: string,
  context: LocalLibraryHealthNoticeContext,
): LocalLibraryHealthNotice {
  const detailSignature = extractHealthDetailSignature(detail);
  const classification = classifyLocalLibraryHealthFailure(detail, context);
  return {
    classification,
    detailSignature,
    messageKey: classification === 'benign_empty'
      ? 'library_health.benign_empty'
      : 'library_health.genuine_warning',
    severity: classification === 'benign_empty' ? 'info' : 'warning',
  };
}

export function logLocalLibraryHealthNoticeRendered(notice: LocalLibraryHealthNotice): void {
  diagnosticLog(
    notice.classification === 'benign_empty' ? 'info' : 'warn',
    'other',
    '[health] toast_rendered',
    {
      classification: notice.classification,
      detail_signature: notice.detailSignature,
    },
  );
}

/** @internal test helper — canonical Bob Farrand field-case detail string. */
export function benignEmptyFieldCaseDetail(): string {
  return `${BOB_FIELD_CASE} — restart Maestro; if this persists, contact support`;
}
